import { useCallback, useEffect, useId, useMemo, useRef, useState, type FormEvent, type KeyboardEvent as ReactKeyboardEvent, type ReactNode, type RefObject } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import {
  Building2,
  Check,
  Copy,
  Crown,
  Eye,
  Globe,
  KeyRound,
  Layers,
  Link2,
  Loader2,
  Lock,
  LogOut,
  Pencil,
  Plus,
  Search,
  ShieldCheck,
  Ticket,
  Trash2,
  UserMinus,
  UserPlus,
} from 'lucide-react';
import type { Division, Mailbox, WorkspaceDetail, WorkspaceInvite, WorkspaceMember, WorkspaceRole, WorkspaceSummary } from '@mailpilot/shared';
import { api, ApiError, errorMessage, type BodyOf } from '../../lib/api';
import { qk } from '../../lib/keys';
import { copyText } from '../../lib/clipboard';
import { fullDate, isValidEmail, relativeTime } from '../../lib/format';
import { useMe } from '../../hooks/useMe';
import { useMailboxes } from '../../hooks/useMailboxes';
import {
  inviteLink,
  useCreateOrganization,
  useCurrentWorkspace,
  useEnterWorkspace,
  useJoinOrganization,
  useWorkspaceDetail,
} from '../../hooks/useWorkspace';
import {
  AccountBadge,
  Avatar,
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  ChipInput,
  EmptyState,
  ErrorState,
  Field,
  IconButton,
  InlineError,
  Input,
  Modal,
  Segmented,
  Select,
  Skeleton,
  Textarea,
  spring,
  useConfirm,
  useToast,
} from '../../components/ui';
import { DangerCard, SettingRow } from '../../components/settings/SettingsPrimitives';
import { SettingsTitle } from './SettingsLayout';

// ---------------------------------------------------------------------------
// Constants & small helpers
// ---------------------------------------------------------------------------

const PALETTE = ['#1F6FEB', '#4F46E5', '#7C3AED', '#E11D48', '#EA580C', '#059669', '#0D9488', '#475569'];
const COLOR_NAMES: Record<string, string> = {
  '#1F6FEB': 'Blue',
  '#4F46E5': 'Indigo',
  '#7C3AED': 'Violet',
  '#E11D48': 'Rose',
  '#EA580C': 'Orange',
  '#059669': 'Green',
  '#0D9488': 'Teal',
  '#475569': 'Slate',
};
const STARTER_DIVISIONS = ['Sales', 'Support', 'Finance', 'Operations'];
const ROLE_LABEL: Record<WorkspaceRole, string> = { owner: 'Owner', admin: 'Admin', member: 'Member' };
const VISIBILITY_NOTE = 'Admins and the owner can see every account. Members see shared accounts plus those of their divisions.';
const SIGNUP_NOTE = 'People can also enter this code when they sign up.';

type Tab = 'members' | 'divisions' | 'invites';
const TABS: Tab[] = ['members', 'divisions', 'invites'];
const TAB_TITLES: Record<Tab, string> = { members: 'Members', divisions: 'Divisions', invites: 'Invites' };

/** Row/card actions: always usable, dimmed on hover-capable devices until the row is hovered or focused. */
const REVEAL = 'transition-opacity [@media(hover:hover)]:opacity-50 group-hover:opacity-100 group-focus-within:opacity-100';
const ROW = 'group transition-colors hover:bg-raised/40 focus-within:bg-raised/40';
const LABEL = 'text-[0.8125rem] font-medium text-ink';

type Assignable = Exclude<WorkspaceRole, 'owner'>;

const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? '' : 's'}`;
const sameSet = (a: string[], b: string[]) => a.length === b.length && a.every((x) => b.includes(x));
const toggle = (list: string[], id: string, on: boolean) => (on ? (list.includes(id) ? list : [...list, id]) : list.filter((x) => x !== id));
const mailboxLabel = (m: Mailbox) => m.displayName.trim() || m.email;

function byId<T extends { id: string }>(items: T[]): Map<string, T> {
  return new Map(items.map((x) => [x.id, x]));
}

/** Mirrors the server: accepts lowercase, spaces or a whole invite link. */
function normaliseCode(input: string): string {
  const raw = input.trim().split(/[/?#=]/).filter(Boolean).pop() ?? '';
  const s = raw.toUpperCase().replace(/[^A-Z0-9]/g, '');
  return s.length === 12 ? `${s.slice(0, 4)}-${s.slice(4, 8)}-${s.slice(8, 12)}` : s;
}
const CODE_RE = /^[A-Z0-9]{4}-[A-Z0-9]{4}-[A-Z0-9]{4}$/;

function nextColor(used: string[]): string {
  const taken = new Set(used.map((c) => c.toUpperCase()));
  return PALETTE.find((c) => !taken.has(c)) ?? PALETTE[used.length % PALETTE.length];
}

function usesText(i: WorkspaceInvite): string {
  if (i.maxUses === null) return i.uses ? `Used ${i.uses}× · no limit` : 'Unlimited uses';
  if (i.maxUses === 1) return 'Single use';
  return `${i.uses} of ${i.maxUses} used`;
}

function expiryText(i: WorkspaceInvite): string {
  return i.expiresAt ? `Expires ${relativeTime(i.expiresAt)}` : 'Never expires';
}

/** Members, divisions and mailbox visibility all hang off these three queries. */
function useInvalidateOrg() {
  const qc = useQueryClient();
  return useCallback(async () => {
    await Promise.all([qk.workspace, qk.mailboxes, qk.me].map((queryKey) => qc.invalidateQueries({ queryKey })));
  }, [qc]);
}

function setDetail(qc: QueryClient, fn: (d: WorkspaceDetail) => WorkspaceDetail) {
  qc.setQueryData<WorkspaceDetail>(qk.workspace, (d) => (d ? fn(d) : d));
}

function useCopy() {
  const toast = useToast();
  const [copied, setCopied] = useState<string | null>(null);
  const timer = useRef<number>();
  useEffect(() => () => window.clearTimeout(timer.current), []);
  const copy = useCallback(
    async (text: string, key: string, message: string) => {
      try {
        await copyText(text);
        setCopied(key);
        window.clearTimeout(timer.current);
        timer.current = window.setTimeout(() => setCopied(null), 1600);
        toast.success(message);
      } catch {
        toast.error('Could not copy automatically — select the code and copy it manually.');
      }
    },
    [toast],
  );
  return { copy, copied };
}

/** State for a modal that remounts (fresh form) each time it opens but keeps its content during the exit animation. */
function useModalState<T>(initial: T) {
  const [state, setState] = useState<{ open: boolean; value: T; key: number }>({ open: false, value: initial, key: 0 });
  const open = useCallback((value: T) => setState((s) => ({ open: true, value, key: s.key + 1 })), []);
  const close = useCallback(() => setState((s) => ({ ...s, open: false })), []);
  return { ...state, show: open, close };
}

// ---------------------------------------------------------------------------
// Presentational pieces
// ---------------------------------------------------------------------------

function Dot({ color, className }: { color: string; className?: string }) {
  return <span aria-hidden className={clsx('inline-block h-2 w-2 shrink-0 rounded-full', className)} style={{ background: color }} />;
}

function DivisionChip({ division }: { division: Pick<Division, 'name' | 'color'> }) {
  return (
    <span title={division.name} className="inline-flex max-w-[11rem] items-center gap-1.5 rounded-full border border-line bg-surface px-2 py-0.5 text-[0.6875rem] font-medium text-muted">
      <Dot color={division.color} />
      <span className="truncate">{division.name}</span>
    </span>
  );
}

function MoreChip({ n, title }: { n: number; title?: string }) {
  if (n <= 0) return null;
  return (
    <span title={title} className="inline-flex items-center rounded-full bg-sunken px-2 py-0.5 text-[0.6875rem] font-medium text-muted">
      +{n}
    </span>
  );
}

function RoleBadge({ role, title }: { role: WorkspaceRole; title?: string }) {
  if (role === 'owner')
    return (
      <Badge tone="primary" title={title} icon={<Crown className="h-3 w-3" aria-hidden />}>
        Owner
      </Badge>
    );
  if (role === 'admin')
    return (
      <Badge tone="info" title={title} icon={<ShieldCheck className="h-3 w-3" aria-hidden />}>
        Admin
      </Badge>
    );
  return <Badge title={title}>Member</Badge>;
}

function WorkspaceTile({ name, color, size = 44 }: { name: string; color: string; size?: number }) {
  return (
    <span
      aria-hidden
      className={clsx('inline-flex shrink-0 select-none items-center justify-center font-semibold text-white shadow-sm', size >= 40 ? 'rounded-2xl' : 'rounded-xl')}
      style={{ width: size, height: size, background: color, fontSize: Math.round(size * 0.42) }}
    >
      {(name.trim()[0] ?? '?').toUpperCase()}
    </span>
  );
}

function AvatarStack({ people, max = 5 }: { people: WorkspaceMember[]; max?: number }) {
  const shown = people.slice(0, max);
  return (
    <div role="img" aria-label={`Members: ${people.map((p) => p.displayName).join(', ')}`} className="flex items-center">
      <div className="flex -space-x-2">
        {shown.map((p) => (
          <span key={p.userId} title={p.displayName}>
            <Avatar name={p.displayName} email={p.email} size={26} ring="rgb(var(--surface))" />
          </span>
        ))}
      </div>
      {people.length > max && <span className="ml-1.5 text-xs font-medium text-muted">+{people.length - max}</span>}
    </div>
  );
}

/** Colour picker: a radio group with roving focus and arrow-key support. */
function ColorSwatches({ value, onChange, label, disabled }: { value: string; onChange: (c: string) => void; label: string; disabled?: boolean }) {
  const labelId = useId();
  const refs = useRef<(HTMLButtonElement | null)[]>([]);
  const current = value.toUpperCase();
  const colors = PALETTE.includes(current) ? PALETTE : [current, ...PALETTE];
  const index = Math.max(0, colors.indexOf(current));
  const onKeyDown = (e: ReactKeyboardEvent<HTMLDivElement>) => {
    const step = e.key === 'ArrowRight' || e.key === 'ArrowDown' ? 1 : e.key === 'ArrowLeft' || e.key === 'ArrowUp' ? -1 : 0;
    if (!step || disabled) return;
    e.preventDefault();
    const next = (index + step + colors.length) % colors.length;
    onChange(colors[next]);
    refs.current[next]?.focus();
  };
  return (
    <div>
      <p id={labelId} className={clsx(LABEL, 'mb-2')}>
        {label}
      </p>
      <div role="radiogroup" aria-labelledby={labelId} onKeyDown={onKeyDown} className="flex flex-wrap gap-2">
        {colors.map((c, i) => {
          const active = c === current;
          return (
            <button
              key={c}
              ref={(el) => {
                refs.current[i] = el;
              }}
              type="button"
              role="radio"
              aria-checked={active}
              aria-label={COLOR_NAMES[c] ?? c}
              tabIndex={active ? 0 : -1}
              disabled={disabled}
              onClick={() => onChange(c)}
              className={clsx(
                'flex h-8 w-8 items-center justify-center rounded-full ring-offset-2 ring-offset-surface transition-shadow focus:outline-none focus-visible:ring-2 focus-visible:ring-primary disabled:opacity-50',
                active ? 'ring-2 ring-ink' : 'hover:ring-2 hover:ring-line',
              )}
              style={{ background: c }}
            >
              {active && <Check className="h-4 w-4 text-white" aria-hidden />}
            </button>
          );
        })}
      </div>
    </div>
  );
}

/** Full-width checkbox row (the whole row is the label). */
function CheckRow({ checked, onChange, disabled, children }: { checked: boolean; onChange: (v: boolean) => void; disabled?: boolean; children: ReactNode }) {
  return (
    <label className={clsx('flex items-center gap-3 rounded-xl px-2.5 py-2 transition-colors', disabled ? 'opacity-60' : 'cursor-pointer hover:bg-surface has-[:focus-visible]:bg-surface')}>
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
        className="h-[18px] w-[18px] shrink-0 rounded accent-[rgb(var(--primary))]"
      />
      <span className="flex min-w-0 flex-1 items-center gap-2.5">{children}</span>
    </label>
  );
}

/** Compact checkbox rendered as a pill. */
function TogglePill({ checked, onChange, children }: { checked: boolean; onChange: (v: boolean) => void; children: ReactNode }) {
  return (
    <label
      className={clsx(
        'inline-flex cursor-pointer items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium transition-colors has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-primary/40',
        checked ? 'border-primary/50 bg-primary-soft text-primary-ink' : 'border-line bg-surface text-muted hover:bg-raised hover:text-ink',
      )}
    >
      <input type="checkbox" className="sr-only" checked={checked} onChange={(e) => onChange(e.target.checked)} />
      {checked && <Check className="h-3 w-3" aria-hidden />}
      {children}
    </label>
  );
}

/** Labelled group inside forms. */
function Section({ title, hint, aside, children }: { title: ReactNode; hint?: ReactNode; aside?: ReactNode; children: ReactNode }) {
  const id = useId();
  return (
    <div role="group" aria-labelledby={id} className="space-y-2">
      <div className="flex items-end justify-between gap-3">
        <div className="min-w-0">
          <p id={id} className={LABEL}>
            {title}
          </p>
          {hint && <p className="mt-0.5 text-xs text-muted">{hint}</p>}
        </div>
        {aside}
      </div>
      {children}
    </div>
  );
}

function SearchBox({ value, onChange, label, className }: { value: string; onChange: (v: string) => void; label: string; className?: string }) {
  return (
    <div className={clsx('relative', className)}>
      <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-subtle" aria-hidden />
      <Input type="search" aria-label={label} placeholder={label} value={value} onChange={(e) => onChange(e.target.value)} className="!h-9 pl-9 text-[0.8125rem]" />
    </div>
  );
}

function TabLabel({ text, count }: { text: string; count?: number }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      {text}
      {count !== undefined && <span className="tabular-nums opacity-70">{count}</span>}
    </span>
  );
}

function ListSkeleton({ rows = 4 }: { rows?: number }) {
  return (
    <Card>
      <div className="divide-y divide-line" aria-hidden>
        {Array.from({ length: rows }).map((_, i) => (
          <div key={i} className="flex items-center gap-3 px-6 py-3.5">
            <Skeleton className="h-9 w-9 rounded-full" />
            <div className="flex-1 space-y-2">
              <Skeleton className="h-3.5 w-40" />
              <Skeleton className="h-3 w-56 max-w-full" />
            </div>
            <Skeleton className="h-6 w-16 rounded-full" />
          </div>
        ))}
      </div>
    </Card>
  );
}

function PageSkeleton() {
  return (
    <div className="space-y-6" aria-busy="true">
      <Card>
        <CardBody className="flex items-center gap-4">
          <Skeleton className="h-12 w-12 rounded-2xl" />
          <div className="flex-1 space-y-2">
            <Skeleton className="h-4 w-1/3" />
            <Skeleton className="h-3 w-1/4" />
          </div>
        </CardBody>
      </Card>
      <Skeleton className="h-10 w-72 max-w-full rounded-xl" />
      <ListSkeleton />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export function OrganizationSettings() {
  const me = useMe();
  const { workspace } = useCurrentWorkspace();

  if (me.isLoading) {
    return (
      <div>
        <SettingsTitle title="Organization & team" />
        <PageSkeleton />
      </div>
    );
  }
  if (me.isError || !me.data || !workspace) {
    return (
      <div>
        <SettingsTitle title="Organization & team" />
        <Card>
          <ErrorState error={me.error} onRetry={() => void me.refetch()} title="Couldn't load your workspace" />
        </Card>
      </div>
    );
  }
  if (workspace.kind === 'personal') return <PersonalWorkspace />;
  return <OrganizationWorkspace key={workspace.id} summary={workspace} meId={me.data.user.id} />;
}

// ---------------------------------------------------------------------------
// Personal workspace: create or join an organization
// ---------------------------------------------------------------------------

function PersonalWorkspace() {
  const modal = useModalState<'create' | 'join'>('create');
  return (
    <div>
      <SettingsTitle
        title="Organization & team"
        description="Your personal workspace stays private to you. Organizations are separate, shared workspaces you can switch between."
      />
      <Card>
        <EmptyState
          className="!py-14"
          icon={<Building2 className="h-6 w-6" aria-hidden />}
          title="Bring your team together"
          description="An organization shares its email accounts with everyone you invite. Divisions such as Sales or Support keep some accounts visible only to their team."
          actions={
            <>
              <Button variant="primary" icon={<Plus className="h-4 w-4" aria-hidden />} onClick={() => modal.show('create')}>
                Create an organization
              </Button>
              <Button icon={<KeyRound className="h-4 w-4" aria-hidden />} onClick={() => modal.show('join')}>
                Join with an invite code
              </Button>
            </>
          }
        />
      </Card>
      <CreateOrganizationModal key={`create-${modal.key}`} open={modal.open && modal.value === 'create'} onClose={modal.close} />
      <JoinOrganizationModal key={`join-${modal.key}`} open={modal.open && modal.value === 'join'} onClose={modal.close} />
    </div>
  );
}

function CreateOrganizationModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const create = useCreateOrganization();
  const toast = useToast();
  const formId = useId();
  const [name, setName] = useState('');
  const [color, setColor] = useState(PALETTE[1]);
  const [divisions, setDivisions] = useState<string[]>([]);
  const [error, setError] = useState<unknown>(null);
  const trimmed = name.trim();

  // Same clean-up the server applies: trimmed, ≤60 chars, case-insensitively unique, max 20.
  const cleanDivisions = useMemo(() => {
    const out = new Map<string, string>();
    for (const d of divisions.map((x) => x.trim().slice(0, 60)).filter(Boolean)) if (!out.has(d.toLowerCase())) out.set(d.toLowerCase(), d);
    return [...out.values()].slice(0, 20);
  }, [divisions]);
  const suggestions = STARTER_DIVISIONS.filter((s) => !divisions.some((d) => d.trim().toLowerCase() === s.toLowerCase()));

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!trimmed || create.isPending) return;
    setError(null);
    try {
      await create.mutateAsync({ name: trimmed, color, divisions: cleanDivisions });
      toast.success(`${trimmed} is ready. Invite your team from the Invites tab.`);
    } catch (err) {
      setError(err);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Create an organization"
      description="You'll be its owner and can invite people straight away."
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="submit" form={formId} variant="primary" loading={create.isPending} disabled={!trimmed}>
            Create organization
          </Button>
        </>
      }
    >
      <form id={formId} onSubmit={submit} className="space-y-5">
        <div className="flex items-end gap-3">
          <WorkspaceTile name={trimmed || '?'} color={color} size={40} />
          <Field label="Organization name" required className="min-w-0 flex-1">
            {(id) => (
              <Input
                id={id}
                data-autofocus
                value={name}
                maxLength={80}
                placeholder="Acme Inc."
                autoComplete="organization"
                onChange={(e) => {
                  setName(e.target.value);
                  setError(null);
                }}
              />
            )}
          </Field>
        </div>
        <ColorSwatches label="Colour" value={color} onChange={setColor} />
        <Field label="Starter divisions (optional)" hint="Teams like Sales or Support. Press Enter or comma to add. You can change these later.">
          {(id) => (
            <div className="space-y-2">
              <ChipInput id={id} label="Starter divisions" value={divisions} onChange={setDivisions} placeholder="e.g. Sales, Support" />
              {suggestions.length > 0 && (
                <div className="flex flex-wrap items-center gap-1.5">
                  <span className="text-xs text-subtle">Quick add:</span>
                  {suggestions.map((s) => (
                    <button
                      key={s}
                      type="button"
                      aria-label={`Add ${s} division`}
                      onClick={() => setDivisions([...divisions, s])}
                      className="inline-flex items-center gap-1 rounded-full border border-dashed border-line px-2 py-0.5 text-xs font-medium text-muted transition-colors hover:border-primary/40 hover:bg-primary-soft hover:text-primary-ink"
                    >
                      <Plus className="h-3 w-3" aria-hidden />
                      {s}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </Field>
        {divisions.length > 20 && <p className="text-xs text-warn-ink">Only the first 20 divisions will be created.</p>}
        {error ? <InlineError error={error} /> : null}
      </form>
    </Modal>
  );
}

function JoinOrganizationModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const join = useJoinOrganization();
  const toast = useToast();
  const formId = useId();
  const [raw, setRaw] = useState('');
  const [error, setError] = useState<unknown>(null);
  const code = normaliseCode(raw);
  const valid = CODE_RE.test(code);

  // Public preview so people can see what they're joining before they commit.
  const preview = useQuery({
    queryKey: ['invite-preview', code],
    queryFn: () => api('GET /api/invites/:code', { params: { code } }),
    enabled: open && valid,
    retry: false,
    staleTime: 60_000,
  });
  const previewInvalid = preview.isError && preview.error instanceof ApiError && preview.error.status === 404;

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!valid || join.isPending) return;
    setError(null);
    try {
      const r = await join.mutateAsync(code);
      toast.success(`Welcome to ${r.workspace.name}`);
    } catch (err) {
      setError(err);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="sm"
      title="Join an organization"
      description="Enter the invite code an admin shared with you."
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="submit" form={formId} variant="primary" loading={join.isPending} disabled={!valid || previewInvalid}>
            Join
          </Button>
        </>
      }
    >
      <form id={formId} onSubmit={submit} className="space-y-5">
        <Field label="Invite code" hint="Paste the code or the whole invite link. Capitals and spaces don't matter.">
          {(id) => (
            <Input
              id={id}
              data-autofocus
              value={raw}
              placeholder="ABCD-EFGH-JKLM"
              autoComplete="off"
              autoCapitalize="characters"
              spellCheck={false}
              className="font-mono tracking-wider"
              onChange={(e) => {
                setRaw(e.target.value);
                setError(null);
              }}
            />
          )}
        </Field>
        {valid && code !== raw.trim() && (
          <p className="text-xs text-muted">
            Using code <span className="font-mono font-semibold text-ink">{code}</span>
          </p>
        )}
        {valid && (
          <div aria-live="polite">
            {preview.isLoading ? (
              <p className="flex items-center gap-2 text-[0.8125rem] text-muted">
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                Checking code…
              </p>
            ) : preview.data ? (
              <div className="flex items-center gap-3 rounded-2xl bg-raised p-3">
                <WorkspaceTile name={preview.data.workspaceName} color={preview.data.workspaceColor} size={36} />
                <div className="min-w-0">
                  <p className="truncate font-medium text-ink">{preview.data.workspaceName}</p>
                  <p className="text-xs text-muted">
                    You'll join as {ROLE_LABEL[preview.data.role].toLowerCase()}
                    {preview.data.invitedBy ? ` · invited by ${preview.data.invitedBy}` : ''}
                    {preview.data.emailLocked ? ' · for a specific email address' : ''}
                  </p>
                </div>
              </div>
            ) : previewInvalid ? (
              <p className="text-[0.8125rem] text-danger-ink">{errorMessage(preview.error)}</p>
            ) : null}
          </div>
        )}
        {error ? <InlineError error={error} /> : null}
      </form>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Organization workspace
// ---------------------------------------------------------------------------

function OrganizationWorkspace({ summary, meId }: { summary: WorkspaceSummary; meId: string }) {
  const detailQ = useWorkspaceDetail(true);
  const mailboxesQ = useMailboxes();
  const [params, setParams] = useSearchParams();
  const detail = detailQ.data;
  const ws = detail?.workspace ?? summary;
  const isAdmin = ws.role === 'owner' || ws.role === 'admin';
  const mailboxes = mailboxesQ.data?.mailboxes ?? [];

  const requested = params.get('tab') as Tab | null;
  const tab: Tab = requested && TABS.includes(requested) ? requested : 'members';
  const setTab = useCallback(
    (t: Tab) =>
      setParams(
        (p) => {
          const next = new URLSearchParams(p);
          if (t === 'members') next.delete('tab');
          else next.set('tab', t);
          return next;
        },
        { replace: true },
      ),
    [setParams],
  );

  const divisionModal = useModalState<Division | null>(null);
  const inviteModal = useModalState<null>(null);
  const memberModal = useModalState<WorkspaceMember | null>(null);
  const { show: showDivision } = divisionModal;
  const { show: showInvite } = inviteModal;
  const { close: closeMember } = memberModal;

  const [newInviteId, setNewInviteId] = useState<string | null>(null);
  useEffect(() => {
    if (!newInviteId) return;
    const t = window.setTimeout(() => setNewInviteId(null), 15_000);
    return () => window.clearTimeout(t);
  }, [newInviteId]);

  const createDivisionFromMember = useCallback(() => {
    closeMember();
    setTab('divisions');
    showDivision(null);
  }, [closeMember, setTab, showDivision]);

  const memberCount = detail?.members.length ?? ws.memberCount;

  let action: ReactNode = null;
  if (isAdmin && detail) {
    if (tab === 'members')
      action = (
        <Button
          size="sm"
          variant="primary"
          icon={<UserPlus className="h-4 w-4" aria-hidden />}
          onClick={() => {
            setTab('invites');
            showInvite(null);
          }}
        >
          Invite people
        </Button>
      );
    else if (tab === 'divisions')
      action = (
        <Button size="sm" variant="primary" icon={<Plus className="h-4 w-4" aria-hidden />} onClick={() => showDivision(null)}>
          New division
        </Button>
      );
    else
      action = (
        <Button size="sm" variant="primary" icon={<Ticket className="h-4 w-4" aria-hidden />} onClick={() => showInvite(null)}>
          Create invite
        </Button>
      );
  }

  return (
    <div>
      <SettingsTitle title="Organization & team" description={`Members, divisions and invites for ${ws.name}.`} />
      <div className="space-y-6">
        <OrgHeaderCard workspace={ws} memberCount={memberCount} isAdmin={isAdmin} />

        {detailQ.isLoading ? (
          <>
            <Skeleton className="h-10 w-72 max-w-full rounded-xl" />
            <ListSkeleton />
          </>
        ) : detailQ.isError || !detail ? (
          <Card>
            <ErrorState error={detailQ.error} onRetry={() => void detailQ.refetch()} title="Couldn't load your organization" />
          </Card>
        ) : (
          <>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <Segmented<Tab>
                label="Organization sections"
                value={tab}
                onChange={setTab}
                options={[
                  { value: 'members', label: <TabLabel text="Members" count={detail.members.length} /> },
                  { value: 'divisions', label: <TabLabel text="Divisions" count={detail.divisions.length} /> },
                  { value: 'invites', label: <TabLabel text="Invites" count={isAdmin ? detail.invites.length : undefined} /> },
                ]}
              />
              {action}
            </div>

            <AnimatePresence mode="wait" initial={false}>
              <motion.section
                key={tab}
                aria-label={TAB_TITLES[tab]}
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -4 }}
                transition={{ duration: 0.16, ease: 'easeOut' }}
              >
                {tab === 'members' && (
                  <MembersPanel
                    members={detail.members}
                    divisions={detail.divisions}
                    meId={meId}
                    isAdmin={isAdmin}
                    isOwner={ws.role === 'owner'}
                    onEditDivisions={memberModal.show}
                    onInvite={() => {
                      setTab('invites');
                      showInvite(null);
                    }}
                  />
                )}
                {tab === 'divisions' && (
                  <DivisionsPanel
                    members={detail.members}
                    divisions={detail.divisions}
                    mailboxes={mailboxes}
                    meId={meId}
                    isAdmin={isAdmin}
                    onNew={() => showDivision(null)}
                    onEdit={showDivision}
                  />
                )}
                {tab === 'invites' && (
                  <InvitesPanel invites={detail.invites} divisions={detail.divisions} isAdmin={isAdmin} newInviteId={newInviteId} onCreate={() => showInvite(null)} />
                )}
              </motion.section>
            </AnimatePresence>

            <LeaveCard workspace={ws} members={detail.members} meId={meId} />

            {isAdmin && (
              <>
                <DivisionModal
                  key={`division-${divisionModal.key}`}
                  open={divisionModal.open}
                  onClose={divisionModal.close}
                  division={divisionModal.value}
                  divisions={detail.divisions}
                  members={detail.members}
                  mailboxes={mailboxes}
                  mailboxesLoading={mailboxesQ.isLoading}
                  mailboxesError={mailboxesQ.error}
                />
                <InviteModal
                  key={`invite-${inviteModal.key}`}
                  open={inviteModal.open}
                  onClose={inviteModal.close}
                  divisions={detail.divisions}
                  workspaceName={ws.name}
                  onCreated={setNewInviteId}
                />
                {memberModal.value && (
                  <MemberDivisionsModal
                    key={`member-${memberModal.key}`}
                    open={memberModal.open}
                    onClose={memberModal.close}
                    member={memberModal.value}
                    divisions={detail.divisions}
                    onCreateDivision={createDivisionFromMember}
                  />
                )}
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// ---------- header ----------

function OrgHeaderCard({ workspace, memberCount, isAdmin }: { workspace: WorkspaceSummary; memberCount: number; isAdmin: boolean }) {
  const toast = useToast();
  const invalidate = useInvalidateOrg();
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(workspace.name);
  const [color, setColor] = useState(workspace.color);
  const editRef = useRef<HTMLButtonElement>(null);
  const wasEditing = useRef(false);

  useEffect(() => {
    if (wasEditing.current && !editing) editRef.current?.focus();
    wasEditing.current = editing;
  }, [editing]);

  const trimmed = name.trim();
  const nameChanged = trimmed !== workspace.name;
  const colorChanged = color.toUpperCase() !== workspace.color.toUpperCase();

  const save = useMutation({
    mutationFn: (body: BodyOf<'PATCH /api/workspace'>) => api('PATCH /api/workspace', { body }),
    onSuccess: async (r) => {
      await invalidate();
      setEditing(false);
      toast.success(`Saved ${r.workspace.name}`);
    },
    onError: (e) => toast.error(e),
  });

  const startEdit = () => {
    setName(workspace.name);
    setColor(workspace.color);
    setEditing(true);
  };
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!trimmed || save.isPending) return;
    if (!nameChanged && !colorChanged) return setEditing(false);
    save.mutate({ ...(nameChanged ? { name: trimmed } : {}), ...(colorChanged ? { color } : {}) });
  };

  const shownName = editing ? trimmed || workspace.name : workspace.name;
  const shownColor = editing ? color : workspace.color;

  return (
    <Card className="overflow-hidden">
      <CardBody className="flex flex-wrap items-center gap-4 !py-5">
        <WorkspaceTile name={shownName} color={shownColor} size={48} />
        <div className="min-w-0 flex-1">
          <h3 className="truncate text-[0.9375rem] font-semibold text-ink">{shownName}</h3>
          <p className="mt-0.5 flex flex-wrap items-center gap-x-2 text-[0.8125rem] text-muted">
            <span>{plural(memberCount, 'member')}</span>
            <span className="text-subtle">·</span>
            <RoleBadge role={workspace.role} title="Your role" />
          </p>
        </div>
        {isAdmin && !editing && (
          <Button ref={editRef} size="sm" icon={<Pencil className="h-4 w-4" aria-hidden />} aria-label="Edit organization name and colour" onClick={startEdit}>
            Edit
          </Button>
        )}
      </CardBody>
      <AnimatePresence initial={false}>
        {editing && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={spring} className="overflow-hidden">
            <form
              aria-label="Edit organization"
              onSubmit={submit}
              onKeyDown={(e) => {
                if (e.key === 'Escape') {
                  e.stopPropagation();
                  setEditing(false);
                }
              }}
              className="space-y-5 border-t border-line/70 px-6 pb-6 pt-5"
            >
              <Field label="Organization name" error={!trimmed ? 'Give your organization a name.' : undefined}>
                {(id) => <Input id={id} autoFocus value={name} maxLength={80} onChange={(e) => setName(e.target.value)} className="max-w-md" />}
              </Field>
              <ColorSwatches label="Colour" value={color} onChange={setColor} />
              <div className="flex justify-end gap-2">
                <Button size="sm" onClick={() => setEditing(false)}>
                  Cancel
                </Button>
                <Button size="sm" type="submit" variant="primary" loading={save.isPending} disabled={!trimmed || (!nameChanged && !colorChanged)}>
                  Save
                </Button>
              </div>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </Card>
  );
}

// ---------- members ----------

function MembersPanel({
  members,
  divisions,
  meId,
  isAdmin,
  isOwner,
  onEditDivisions,
  onInvite,
}: {
  members: WorkspaceMember[];
  divisions: Division[];
  meId: string;
  isAdmin: boolean;
  isOwner: boolean;
  onEditDivisions: (m: WorkspaceMember) => void;
  onInvite: () => void;
}) {
  const [query, setQuery] = useState('');
  const divisionsById = useMemo(() => byId(divisions), [divisions]);
  const q = query.trim().toLowerCase();
  const list = q ? members.filter((m) => m.displayName.toLowerCase().includes(q) || m.email.toLowerCase().includes(q)) : members;

  return (
    <Card className="overflow-hidden">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-line/70 bg-raised/40 px-6 py-3.5">
        <p className="flex min-w-0 flex-1 items-start gap-2 text-[0.8125rem] text-muted">
          <Eye className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
          <span>{VISIBILITY_NOTE}</span>
        </p>
        {members.length > 8 && <SearchBox value={query} onChange={setQuery} label="Search members" className="w-full sm:w-56" />}
      </div>
      <ul aria-label="Members" className="divide-y divide-line">
        {list.map((m) => (
          <MemberRow key={m.userId} member={m} divisionsById={divisionsById} isAdmin={isAdmin} isOwner={isOwner} isMe={m.userId === meId} onEditDivisions={onEditDivisions} />
        ))}
      </ul>
      {list.length === 0 && <p className="px-6 py-8 text-center text-[0.8125rem] text-muted">No members match “{query.trim()}”.</p>}
      {isAdmin && members.length === 1 && (
        <div className="flex flex-wrap items-center justify-between gap-2 border-t border-line/70 px-6 py-3.5 text-[0.8125rem] text-muted">
          <span>It's just you so far.</span>
          <button type="button" onClick={onInvite} className="font-semibold text-primary-ink hover:underline">
            Invite your team →
          </button>
        </div>
      )}
    </Card>
  );
}

function MemberRow({
  member: m,
  divisionsById,
  isAdmin,
  isOwner,
  isMe,
  onEditDivisions,
}: {
  member: WorkspaceMember;
  divisionsById: Map<string, Division>;
  isAdmin: boolean;
  /** Owners can hand over ownership (appoint other owners) and change other owners' roles. */
  isOwner: boolean;
  isMe: boolean;
  onEditDivisions: (m: WorkspaceMember) => void;
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const confirm = useConfirm();
  const invalidate = useInvalidateOrg();
  const canChangeRole = isAdmin && !isMe && (m.role !== 'owner' || isOwner);
  const canRemove = isAdmin && !isMe && (m.role !== 'owner' || isOwner);
  const memberDivisions = m.divisionIds.map((id) => divisionsById.get(id)).filter((d): d is Division => !!d);

  const role = useMutation({
    mutationFn: (r: WorkspaceRole) => api('PATCH /api/workspace/members/:userId', { params: { userId: m.userId }, body: { role: r } }),
    onSuccess: (res, r) => {
      setDetail(qc, (d) => ({ ...d, members: d.members.map((x) => (x.userId === res.member.userId ? res.member : x)) }));
      toast.success(`${m.displayName} is now ${r === 'owner' ? 'an owner' : r === 'admin' ? 'an admin' : 'a member'}`);
      void invalidate();
    },
    onError: (e) => toast.error(e),
  });

  const remove = useMutation({
    mutationFn: () => api('DELETE /api/workspace/members/:userId', { params: { userId: m.userId } }),
    onSuccess: () => {
      setDetail(qc, (d) => ({
        ...d,
        members: d.members.filter((x) => x.userId !== m.userId),
        divisions: d.divisions.map((x) => ({ ...x, memberIds: x.memberIds.filter((id) => id !== m.userId) })),
      }));
      toast.success(`${m.displayName} was removed`);
      void invalidate();
    },
    onError: (e) => toast.error(e),
  });

  const onRemove = async () => {
    const ok = await confirm({
      title: `Remove ${m.displayName}?`,
      message: "They lose access to this organization's accounts straight away. You can invite them again later.",
      confirmLabel: 'Remove',
      danger: true,
    });
    if (ok) remove.mutate();
  };

  const shownRole: WorkspaceRole = role.isPending && role.variables ? role.variables : m.role;
  const lockedReason = !isAdmin ? undefined : isMe ? "You can't change your own role. Ask another owner or admin." : m.role === 'owner' ? 'Only an owner can change an owner’s role' : undefined;
  const changeRole = async (r: WorkspaceRole) => {
    if (r === 'owner') {
      const ok = await confirm({
        title: `Make ${m.displayName} an owner?`,
        message: 'Owners have full control of the organization, including appointing other owners. You stay an owner too, and can then leave if you are handing it over.',
        confirmLabel: 'Make owner',
      });
      if (!ok) return;
    }
    role.mutate(r);
  };

  return (
    <li className={clsx(ROW, 'flex flex-wrap items-center gap-x-3.5 gap-y-2 px-4 py-3.5 sm:px-6')}>
      <Avatar name={m.displayName} email={m.email} size={36} />
      <div className="min-w-0 flex-1">
        <p className="flex min-w-0 items-center gap-1.5 font-medium text-ink">
          <span className="truncate">{m.displayName}</span>
          {isMe && <span className="shrink-0 text-xs font-normal text-subtle">(you)</span>}
        </p>
        <p className="truncate text-[0.8125rem] text-muted">{m.email}</p>
        {memberDivisions.length > 0 && (
          <div className="mt-1.5 flex flex-wrap gap-1" aria-label="Divisions">
            {memberDivisions.slice(0, 4).map((d) => (
              <DivisionChip key={d.id} division={d} />
            ))}
            <MoreChip n={memberDivisions.length - 4} title={memberDivisions.slice(4).map((d) => d.name).join(', ')} />
          </div>
        )}
      </div>
      <div className="ml-auto flex items-center gap-1.5">
        {canChangeRole ? (
          <Select
            aria-label={`Role for ${m.displayName}`}
            value={shownRole}
            disabled={role.isPending || remove.isPending}
            onChange={(e) => void changeRole(e.target.value as WorkspaceRole)}
            className="!h-8 !w-auto !rounded-lg !pl-2.5 text-[0.8125rem]"
          >
            {isOwner && <option value="owner">Owner</option>}
            <option value="admin">Admin</option>
            <option value="member">Member</option>
          </Select>
        ) : (
          <RoleBadge role={m.role} title={lockedReason} />
        )}
        {isAdmin && (
          <div className={clsx('flex items-center', REVEAL)}>
            <IconButton size="sm" label={`Edit divisions for ${m.displayName}`} icon={<Layers className="h-4 w-4" aria-hidden />} onClick={() => onEditDivisions(m)} />
            {canRemove ? (
              <IconButton
                size="sm"
                label={`Remove ${m.displayName}`}
                icon={remove.isPending ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> : <UserMinus className="h-4 w-4" aria-hidden />}
                disabled={remove.isPending}
                onClick={() => void onRemove()}
                className="hover:!bg-danger-soft hover:!text-danger-ink"
              />
            ) : (
              <span className="h-8 w-8" aria-hidden />
            )}
          </div>
        )}
      </div>
    </li>
  );
}

function MemberDivisionsModal({
  open,
  onClose,
  member,
  divisions,
  onCreateDivision,
}: {
  open: boolean;
  onClose: () => void;
  member: WorkspaceMember;
  divisions: Division[];
  onCreateDivision: () => void;
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const invalidate = useInvalidateOrg();
  const [selected, setSelected] = useState<string[]>(member.divisionIds);
  const changed = !sameSet(selected, member.divisionIds);
  const elevated = member.role !== 'member';

  const save = useMutation({
    mutationFn: () => api('PATCH /api/workspace/members/:userId', { params: { userId: member.userId }, body: { divisionIds: selected } }),
    onSuccess: async (r) => {
      setDetail(qc, (d) => ({ ...d, members: d.members.map((m) => (m.userId === r.member.userId ? r.member : m)) }));
      await invalidate();
      toast.success(`Updated divisions for ${member.displayName}`);
      onClose();
    },
    onError: (e) => toast.error(e),
  });

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="sm"
      title={`Divisions for ${member.displayName}`}
      description="They can see the accounts of every division they're in, plus all shared accounts."
      footer={
        divisions.length ? (
          <>
            <Button onClick={onClose}>Cancel</Button>
            <Button variant="primary" loading={save.isPending} disabled={!changed} onClick={() => save.mutate()}>
              Save
            </Button>
          </>
        ) : undefined
      }
    >
      {divisions.length === 0 ? (
        <EmptyState
          className="!py-6"
          icon={<Layers className="h-6 w-6" aria-hidden />}
          title="No divisions yet"
          description="Create a division first, then add people to it."
          actions={
            <Button size="sm" variant="primary" icon={<Plus className="h-4 w-4" aria-hidden />} onClick={onCreateDivision}>
              Create a division
            </Button>
          }
        />
      ) : (
        <div className="space-y-3">
          {elevated && (
            <p className="flex items-start gap-2 rounded-xl bg-info-soft px-3 py-2 text-xs text-info-ink">
              <ShieldCheck className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
              {ROLE_LABEL[member.role]}s already see every account, so divisions only group them with a team.
            </p>
          )}
          <div role="group" aria-label="Divisions" className="-mx-2.5 space-y-0.5">
            {divisions.map((d) => (
              <CheckRow key={d.id} checked={selected.includes(d.id)} onChange={(on) => setSelected((s) => toggle(s, d.id, on))}>
                <Dot color={d.color} className="h-2.5 w-2.5" />
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-ink">{d.name}</span>
                  <span className="block truncate text-xs text-muted">
                    {plural(d.memberIds.length, 'member')} · {plural(d.mailboxIds.length, 'account')}
                  </span>
                </span>
              </CheckRow>
            ))}
          </div>
        </div>
      )}
    </Modal>
  );
}

// ---------- divisions ----------

function DivisionsPanel({
  members,
  divisions,
  mailboxes,
  meId,
  isAdmin,
  onNew,
  onEdit,
}: {
  members: WorkspaceMember[];
  divisions: Division[];
  mailboxes: Mailbox[];
  meId: string;
  isAdmin: boolean;
  onNew: () => void;
  onEdit: (d: Division) => void;
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const confirm = useConfirm();
  const invalidate = useInvalidateOrg();
  const membersById = useMemo(() => new Map(members.map((m) => [m.userId, m])), [members]);
  const mailboxesById = useMemo(() => byId(mailboxes), [mailboxes]);
  const shared = mailboxes.filter((m) => m.divisionIds.length === 0);
  const restricted = mailboxes.length - shared.length;

  const remove = useMutation({
    mutationFn: (d: Division) => api('DELETE /api/workspace/divisions/:id', { params: { id: d.id } }),
    onSuccess: async (_r, d) => {
      setDetail(qc, (x) => ({
        ...x,
        divisions: x.divisions.filter((y) => y.id !== d.id),
        members: x.members.map((m) => ({ ...m, divisionIds: m.divisionIds.filter((id) => id !== d.id) })),
      }));
      toast.success(`Deleted “${d.name}”`);
      await invalidate();
    },
    onError: (e) => toast.error(e),
  });

  const onDelete = async (d: Division) => {
    const exclusive = mailboxes.filter((m) => m.divisionIds.length === 1 && m.divisionIds[0] === d.id);
    const ok = await confirm({
      title: `Delete “${d.name}”?`,
      message: (
        <>
          <p>Accounts only this division could see become visible to everyone in the organization.</p>
          {exclusive.length > 0 && (
            <p className="mt-2">
              That includes <span className="font-medium text-ink">{exclusive.map(mailboxLabel).join(', ')}</span>.
            </p>
          )}
        </>
      ),
      confirmLabel: 'Delete division',
      danger: true,
    });
    if (ok) remove.mutate(d);
  };

  return (
    <div className="space-y-6">
      {mailboxes.length > 0 && (
        <div className="flex flex-wrap items-center gap-x-3 gap-y-2 rounded-2xl bg-raised/80 px-4 py-3">
          <span className="flex items-center gap-2.5 text-[0.8125rem] text-ink">
            <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-surface text-muted">
              <Globe className="h-4 w-4" aria-hidden />
            </span>
            <span>
              <span className="font-semibold">Shared with everyone:</span> {shared.length ? plural(shared.length, 'account') : 'no accounts'}
            </span>
          </span>
          {shared.length > 0 && (
            <div className="flex min-w-0 flex-wrap gap-1.5">
              {shared.slice(0, 5).map((m) => (
                <AccountBadge key={m.id} color={m.color} label={mailboxLabel(m)} />
              ))}
              <MoreChip n={shared.length - 5} title={shared.slice(5).map(mailboxLabel).join(', ')} />
            </div>
          )}
          {restricted > 0 && <span className="text-xs text-muted sm:ml-auto">{plural(restricted, 'account')} restricted to divisions</span>}
        </div>
      )}

      {divisions.length === 0 ? (
        <Card>
          <EmptyState
            icon={<Layers className="h-6 w-6" aria-hidden />}
            title="No divisions yet"
            description={
              isAdmin
                ? 'Divisions are teams like Sales or Support. Give a division an email account and only its members, plus admins, can see it.'
                : 'Divisions are teams like Sales or Support. An admin can set them up to keep some accounts within a team.'
            }
            actions={
              isAdmin ? (
                <Button variant="primary" icon={<Plus className="h-4 w-4" aria-hidden />} onClick={onNew}>
                  Create a division
                </Button>
              ) : undefined
            }
          />
        </Card>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2">
          {divisions.map((d) => (
            <DivisionCard
              key={d.id}
              division={d}
              membersById={membersById}
              mailboxesById={mailboxesById}
              mine={d.memberIds.includes(meId)}
              isAdmin={isAdmin}
              deleting={remove.isPending && remove.variables?.id === d.id}
              onEdit={onEdit}
              onDelete={(x) => void onDelete(x)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function DivisionCard({
  division: d,
  membersById,
  mailboxesById,
  mine,
  isAdmin,
  deleting,
  onEdit,
  onDelete,
}: {
  division: Division;
  membersById: Map<string, WorkspaceMember>;
  mailboxesById: Map<string, Mailbox>;
  mine: boolean;
  isAdmin: boolean;
  deleting: boolean;
  onEdit: (d: Division) => void;
  onDelete: (d: Division) => void;
}) {
  const people = d.memberIds.map((id) => membersById.get(id)).filter((m): m is WorkspaceMember => !!m);
  const accounts = d.mailboxIds.map((id) => mailboxesById.get(id)).filter((m): m is Mailbox => !!m);
  // Members only receive the mailboxes they can see; the rest are counted but not named.
  const hidden = d.mailboxIds.length - accounts.length;

  return (
    <Card className="group relative flex flex-col overflow-hidden transition-colors hover:bg-raised/40 focus-within:bg-raised/40">
      <span aria-hidden className="absolute inset-x-0 top-0 h-1" style={{ background: d.color }} />
      <div className="flex flex-1 flex-col gap-3 p-5 pt-6">
        <div className="flex items-start gap-2">
          <div className="min-w-0 flex-1">
            <h3 className="flex min-w-0 items-center gap-2 font-semibold text-ink">
              <Dot color={d.color} className="h-2.5 w-2.5" />
              <span className="truncate">{d.name}</span>
              {mine && (
                <Badge tone="primary" title="You're in this division">
                  You
                </Badge>
              )}
            </h3>
            {d.description && <p className="mt-1 line-clamp-2 text-[0.8125rem] text-muted">{d.description}</p>}
          </div>
          {isAdmin && (
            <div className={clsx('-mr-1.5 -mt-1.5 flex shrink-0 items-center', REVEAL)}>
              <IconButton size="sm" label={`Edit ${d.name}`} icon={<Pencil className="h-4 w-4" aria-hidden />} onClick={() => onEdit(d)} />
              <IconButton
                size="sm"
                label={`Delete ${d.name}`}
                icon={deleting ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> : <Trash2 className="h-4 w-4" aria-hidden />}
                disabled={deleting}
                onClick={() => onDelete(d)}
                className="hover:!bg-danger-soft hover:!text-danger-ink"
              />
            </div>
          )}
        </div>
        <p className="text-xs font-medium text-muted">
          {plural(d.memberIds.length, 'member')} · {plural(d.mailboxIds.length, 'account')}
        </p>
        <div className="mt-auto space-y-2.5">
          {people.length ? <AvatarStack people={people} /> : <p className="text-xs text-subtle">No members yet</p>}
          {accounts.length || hidden > 0 ? (
            <div className="flex flex-wrap gap-1.5">
              {accounts.slice(0, 4).map((m) => (
                <AccountBadge key={m.id} color={m.color} label={mailboxLabel(m)} />
              ))}
              <MoreChip n={accounts.length - 4} title={accounts.slice(4).map(mailboxLabel).join(', ')} />
              {hidden > 0 && (
                <span className="inline-flex items-center gap-1 rounded-full bg-sunken px-2 py-0.5 text-[0.6875rem] font-medium text-muted" title="Accounts you don't have access to">
                  <Lock className="h-3 w-3" aria-hidden />
                  {hidden} private
                </span>
              )}
            </div>
          ) : (
            <p className="text-xs text-subtle">No accounts restricted to this team</p>
          )}
        </div>
      </div>
    </Card>
  );
}

function DivisionModal({
  open,
  onClose,
  division,
  divisions,
  members,
  mailboxes,
  mailboxesLoading,
  mailboxesError,
}: {
  open: boolean;
  onClose: () => void;
  division: Division | null;
  divisions: Division[];
  members: WorkspaceMember[];
  mailboxes: Mailbox[];
  mailboxesLoading: boolean;
  mailboxesError: unknown;
}) {
  const formId = useId();
  const toast = useToast();
  const invalidate = useInvalidateOrg();
  const [name, setName] = useState(division?.name ?? '');
  const [color, setColor] = useState(() => division?.color ?? nextColor(divisions.map((d) => d.color)));
  const [description, setDescription] = useState(division?.description ?? '');
  const [memberIds, setMemberIds] = useState<string[]>(division?.memberIds ?? []);
  const [mailboxIds, setMailboxIds] = useState<string[]>(division?.mailboxIds ?? []);
  const [serverNameError, setServerNameError] = useState<string | null>(null);
  const [memberQuery, setMemberQuery] = useState('');

  const trimmed = name.trim();
  const duplicate = trimmed !== '' && divisions.some((x) => x.id !== division?.id && x.name.trim().toLowerCase() === trimmed.toLowerCase());
  const nameError = duplicate ? `A division called “${trimmed}” already exists.` : serverNameError;
  const divisionsById = useMemo(() => byId(divisions), [divisions]);
  const mailboxesById = useMemo(() => byId(mailboxes), [mailboxes]);

  const changes = useMemo(() => {
    const body: BodyOf<'PATCH /api/workspace/divisions/:id'> = {};
    if (!division) return body;
    if (trimmed !== division.name) body.name = trimmed;
    if (color.toUpperCase() !== division.color.toUpperCase()) body.color = color;
    if (description.trim() !== division.description) body.description = description.trim();
    if (!sameSet(memberIds, division.memberIds)) body.memberIds = memberIds;
    if (!sameSet(mailboxIds, division.mailboxIds)) body.mailboxIds = mailboxIds;
    return body;
  }, [division, trimmed, color, description, memberIds, mailboxIds]);
  const dirty = !division || Object.keys(changes).length > 0;

  // What this save does to who-sees-what.
  const original = division?.mailboxIds ?? [];
  const newlyRestricted = mailboxIds.filter((id) => !original.includes(id) && mailboxesById.get(id)?.divisionIds.length === 0);
  const newlyShared = original.filter((id) => {
    const mb = mailboxesById.get(id);
    return !mailboxIds.includes(id) && !!mb && mb.divisionIds.every((x) => x === division?.id);
  });

  const save = useMutation({
    mutationFn: () =>
      division
        ? api('PATCH /api/workspace/divisions/:id', { params: { id: division.id }, body: changes })
        : api('POST /api/workspace/divisions', { body: { name: trimmed, color, description: description.trim(), memberIds, mailboxIds } }),
    onSuccess: async (r) => {
      await invalidate();
      toast.success(division ? `Saved “${r.division.name}”` : `Created “${r.division.name}”`);
      onClose();
    },
    onError: (e) => {
      if (e instanceof ApiError && e.status === 409) setServerNameError(e.message);
      else toast.error(e);
    },
  });

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!trimmed || duplicate || !dirty || save.isPending) return;
    save.mutate();
  };

  const mq = memberQuery.trim().toLowerCase();
  const visibleMembers = mq ? members.filter((m) => m.displayName.toLowerCase().includes(mq) || m.email.toLowerCase().includes(mq)) : members;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={division ? `Edit “${division.name}”` : 'New division'}
      description="A division is a team. Accounts you give it are visible only to its members, plus owners and admins."
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="submit" form={formId} variant="primary" loading={save.isPending} disabled={!trimmed || duplicate || !dirty}>
            {division ? 'Save changes' : 'Create division'}
          </Button>
        </>
      }
    >
      <form id={formId} onSubmit={submit} className="space-y-6">
        <div className="space-y-5">
          <Field label="Name" required error={nameError}>
            {(id) => (
              <Input
                id={id}
                data-autofocus
                value={name}
                maxLength={60}
                placeholder="e.g. Support"
                aria-invalid={!!nameError || undefined}
                onChange={(e) => {
                  setName(e.target.value);
                  setServerNameError(null);
                }}
              />
            )}
          </Field>
          <ColorSwatches label="Colour" value={color} onChange={setColor} />
          <Field label="Description (optional)" hint={`${description.length}/280`}>
            {(id) => (
              <Textarea
                id={id}
                value={description}
                maxLength={280}
                rows={2}
                className="!min-h-[64px]"
                placeholder="What this team looks after"
                onChange={(e) => setDescription(e.target.value)}
              />
            )}
          </Field>
        </div>

        <Section
          title={
            <>
              Members <span className="font-normal text-muted">· {memberIds.length} selected</span>
            </>
          }
          aside={
            members.length > 1 ? (
              <button
                type="button"
                className="shrink-0 text-xs font-semibold text-primary-ink hover:underline"
                onClick={() => setMemberIds(memberIds.length === members.length ? [] : members.map((m) => m.userId))}
              >
                {memberIds.length === members.length ? 'Clear' : 'Select all'}
              </button>
            ) : undefined
          }
        >
          {members.length > 8 && <SearchBox value={memberQuery} onChange={setMemberQuery} label="Filter members" />}
          <div className="max-h-56 overflow-y-auto rounded-2xl bg-raised/70 p-1 scroll-thin">
            {visibleMembers.map((m) => (
              <CheckRow key={m.userId} checked={memberIds.includes(m.userId)} onChange={(on) => setMemberIds((s) => toggle(s, m.userId, on))}>
                <Avatar name={m.displayName} email={m.email} size={28} />
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-ink">{m.displayName}</span>
                  <span className="block truncate text-xs text-muted">{m.email}</span>
                </span>
                {m.role !== 'member' && <RoleBadge role={m.role} title="Sees every account anyway" />}
              </CheckRow>
            ))}
            {visibleMembers.length === 0 && <p className="px-3 py-4 text-center text-xs text-muted">No members match.</p>}
          </div>
        </Section>

        <Section title="Accounts" hint="Checked accounts are visible only to this division's members (and to people in any other division they belong to).">
          {mailboxesLoading ? (
            <div className="space-y-2 rounded-2xl bg-raised/70 p-3" aria-hidden>
              <Skeleton className="h-4 w-1/2" />
              <Skeleton className="h-4 w-2/5" />
            </div>
          ) : mailboxesError ? (
            <InlineError error={mailboxesError} />
          ) : mailboxes.length === 0 ? (
            <p className="rounded-2xl bg-raised/70 px-4 py-3 text-[0.8125rem] text-muted">
              No email accounts in this organization yet.{' '}
              <Link to="/settings/accounts" className="font-semibold text-primary-ink hover:underline">
                Connect one
              </Link>
            </p>
          ) : (
            <div className="max-h-56 overflow-y-auto rounded-2xl bg-raised/70 p-1 scroll-thin">
              {mailboxes.map((mb) => {
                const others = mb.divisionIds
                  .filter((x) => x !== division?.id)
                  .map((x) => divisionsById.get(x)?.name)
                  .filter((x): x is string => !!x);
                return (
                  <CheckRow key={mb.id} checked={mailboxIds.includes(mb.id)} onChange={(on) => setMailboxIds((s) => toggle(s, mb.id, on))}>
                    <Dot color={mb.color} className="h-2.5 w-2.5" />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-ink">{mailboxLabel(mb)}</span>
                      <span className="block truncate text-xs text-muted">{mb.email}</span>
                    </span>
                    {mb.divisionIds.length === 0 ? (
                      <Badge icon={<Globe className="h-3 w-3" aria-hidden />} title="Currently visible to everyone in the organization">
                        Everyone
                      </Badge>
                    ) : others.length ? (
                      <span className="max-w-[8rem] truncate text-xs text-subtle" title={`Also in ${others.join(', ')}`}>
                        Also {others.join(', ')}
                      </span>
                    ) : null}
                  </CheckRow>
                );
              })}
            </div>
          )}
          {(newlyRestricted.length > 0 || newlyShared.length > 0) && (
            <div className="space-y-1 text-xs" aria-live="polite">
              {newlyRestricted.length > 0 && (
                <p className="flex items-start gap-1.5 text-warn-ink">
                  <Lock className="mt-0.5 h-3 w-3 shrink-0" aria-hidden />
                  {plural(newlyRestricted.length, 'account')} everyone can see today will become visible only to this division and admins.
                </p>
              )}
              {newlyShared.length > 0 && (
                <p className="flex items-start gap-1.5 text-muted">
                  <Globe className="mt-0.5 h-3 w-3 shrink-0" aria-hidden />
                  {plural(newlyShared.length, 'account')} will become visible to everyone in the organization.
                </p>
              )}
            </div>
          )}
        </Section>
      </form>
    </Modal>
  );
}

// ---------- invites ----------

type Expiry = '7' | '14' | '30' | 'never';
type Uses = '1' | '10' | 'unlimited';

function InvitesPanel({
  invites,
  divisions,
  isAdmin,
  newInviteId,
  onCreate,
}: {
  invites: WorkspaceInvite[];
  divisions: Division[];
  isAdmin: boolean;
  newInviteId: string | null;
  onCreate: () => void;
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const confirm = useConfirm();
  const { copy, copied } = useCopy();
  const divisionsById = useMemo(() => byId(divisions), [divisions]);

  const revoke = useMutation({
    mutationFn: (i: WorkspaceInvite) => api('DELETE /api/workspace/invites/:id', { params: { id: i.id } }),
    onSuccess: (_r, i) => {
      setDetail(qc, (d) => ({ ...d, invites: d.invites.filter((x) => x.id !== i.id) }));
      toast.success('Invite revoked');
      void qc.invalidateQueries({ queryKey: qk.workspace });
    },
    onError: (e) => toast.error(e),
  });

  if (!isAdmin) {
    return (
      <Card>
        <EmptyState icon={<Lock className="h-6 w-6" aria-hidden />} title="Only admins can invite people" description="Ask the owner or an admin for an invite code." />
      </Card>
    );
  }

  const onRevoke = async (i: WorkspaceInvite) => {
    const ok = await confirm({
      title: 'Revoke this invite?',
      message: (
        <>
          {i.code ? (
            <>
              The code <span className="font-mono font-semibold text-ink">{i.code}</span> stops working straight away.
            </>
          ) : (
            'The code stops working straight away.'
          )}{' '}
          People who already joined keep their access.
        </>
      ),
      confirmLabel: 'Revoke',
      danger: true,
    });
    if (ok) revoke.mutate(i);
  };

  return (
    <Card className="overflow-hidden">
      <CardHeader icon={<Ticket className="h-5 w-5" aria-hidden />} title="Active invites" description={SIGNUP_NOTE} />
      {invites.length === 0 ? (
        <EmptyState
          className="!py-8"
          icon={<Ticket className="h-6 w-6" aria-hidden />}
          title="No active invites"
          description="Create an invite code and share it with the people you want to bring in."
          actions={
            <Button variant="primary" icon={<Ticket className="h-4 w-4" aria-hidden />} onClick={onCreate}>
              Create invite
            </Button>
          }
        />
      ) : (
        <>
          <ul aria-label="Active invites" className="mt-5 divide-y divide-line/70 border-t border-line/70">
            {invites.map((i) => {
              const link = i.code ? inviteLink(i.code) : null;
              const divs = i.divisionIds.map((id) => divisionsById.get(id)).filter((d): d is Division => !!d);
              const meta = [usesText(i), expiryText(i), `created ${relativeTime(i.createdAt)}${i.createdByName ? ` by ${i.createdByName}` : ''}`];
              const revoking = revoke.isPending && revoke.variables?.id === i.id;
              return (
                <li key={i.id} className={clsx(ROW, 'flex flex-wrap items-center gap-x-3.5 gap-y-2 px-4 py-3.5 sm:px-6', i.id === newInviteId && 'bg-primary-soft/40')}>
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-primary-soft text-primary-ink">
                    <Ticket className="h-4 w-4" aria-hidden />
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      {i.code ? (
                        <code className="font-mono text-[0.9375rem] font-semibold tracking-wider text-ink">{i.code}</code>
                      ) : (
                        <span className="text-[0.8125rem] italic text-muted">Code unavailable</span>
                      )}
                      <RoleBadge role={i.role} />
                      {i.id === newInviteId && <Badge tone="success">New</Badge>}
                    </div>
                    <p className="mt-0.5 text-xs text-muted">
                      <span>{meta[0]}</span> · <span title={i.expiresAt ? fullDate(i.expiresAt) : undefined}>{meta[1]}</span> ·{' '}
                      <span title={fullDate(i.createdAt)}>{meta[2]}</span>
                    </p>
                    {(i.email || divs.length > 0) && (
                      <div className="mt-1.5 flex flex-wrap gap-1">
                        {i.email && (
                          <span className="inline-flex max-w-[14rem] items-center gap-1 rounded-full bg-sunken px-2 py-0.5 text-[0.6875rem] font-medium text-muted" title={`Only ${i.email} can use this invite`}>
                            <Lock className="h-3 w-3 shrink-0" aria-hidden />
                            <span className="truncate">{i.email}</span>
                          </span>
                        )}
                        {divs.map((d) => (
                          <DivisionChip key={d.id} division={d} />
                        ))}
                      </div>
                    )}
                  </div>
                  <div className={clsx('ml-auto flex items-center', REVEAL)}>
                    <IconButton
                      size="sm"
                      label={i.code ? `Copy code ${i.code}` : 'Copy code'}
                      icon={copied === `code-${i.id}` ? <Check className="h-4 w-4 text-success" aria-hidden /> : <Copy className="h-4 w-4" aria-hidden />}
                      disabled={!i.code}
                      onClick={() => void copy(i.code, `code-${i.id}`, 'Invite code copied')}
                    />
                    {link && (
                      <IconButton
                        size="sm"
                        label="Copy invite link"
                        icon={copied === `link-${i.id}` ? <Check className="h-4 w-4 text-success" aria-hidden /> : <Link2 className="h-4 w-4" aria-hidden />}
                        onClick={() => void copy(link, `link-${i.id}`, 'Invite link copied')}
                      />
                    )}
                    <IconButton
                      size="sm"
                      label={i.code ? `Revoke invite ${i.code}` : 'Revoke invite'}
                      icon={revoking ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> : <Trash2 className="h-4 w-4" aria-hidden />}
                      disabled={revoking}
                      onClick={() => void onRevoke(i)}
                      className="hover:!bg-danger-soft hover:!text-danger-ink"
                    />
                  </div>
                </li>
              );
            })}
          </ul>
          <p className="border-t border-line/70 px-6 py-3 text-xs text-subtle">Expired, used-up and revoked invites are hidden automatically.</p>
        </>
      )}
    </Card>
  );
}

function InviteTicket({
  invite,
  divisions,
  copy,
  copied,
  copyRef,
}: {
  invite: WorkspaceInvite;
  divisions: Division[];
  copy: (text: string, key: string, message: string) => Promise<void>;
  copied: string | null;
  copyRef: RefObject<HTMLButtonElement>;
}) {
  const link = inviteLink(invite.code);
  const divs = invite.divisionIds.map((id) => divisions.find((d) => d.id === id)).filter((d): d is Division => !!d);
  const details = [
    ROLE_LABEL[invite.role],
    divs.length ? `joins ${divs.map((d) => d.name).join(', ')}` : null,
    invite.email ? `only for ${invite.email}` : null,
    usesText(invite).toLowerCase(),
    expiryText(invite).toLowerCase(),
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <div className="space-y-3">
      <div className="relative rounded-2xl border-2 border-dashed border-primary/35 bg-primary-soft/40 px-5 py-6 text-center">
        <span aria-hidden className="absolute -left-3 top-1/2 h-6 w-6 -translate-y-1/2 rounded-full bg-surface" />
        <span aria-hidden className="absolute -right-3 top-1/2 h-6 w-6 -translate-y-1/2 rounded-full bg-surface" />
        <p className="text-[0.6875rem] font-semibold uppercase tracking-[0.14em] text-primary-ink">Invite code</p>
        <p className="mt-2 select-all break-all font-mono text-[1.75rem] font-bold leading-tight tracking-[0.12em] text-ink sm:text-[2rem]">{invite.code}</p>
        <p className="mx-auto mt-2 max-w-sm text-xs text-muted">{details}</p>
        <div className="mt-4 flex flex-wrap justify-center gap-2">
          <Button
            ref={copyRef}
            size="sm"
            variant="primary"
            icon={copied === 'ticket-code' ? <Check className="h-4 w-4" aria-hidden /> : <Copy className="h-4 w-4" aria-hidden />}
            onClick={() => void copy(invite.code, 'ticket-code', 'Invite code copied')}
          >
            Copy code
          </Button>
          {link && (
            <Button
              size="sm"
              icon={copied === 'ticket-link' ? <Check className="h-4 w-4" aria-hidden /> : <Link2 className="h-4 w-4" aria-hidden />}
              onClick={() => void copy(link, 'ticket-link', 'Invite link copied')}
            >
              Copy link
            </Button>
          )}
        </div>
      </div>
      <p className="text-center text-xs text-muted">{SIGNUP_NOTE}</p>
    </div>
  );
}

function InviteModal({
  open,
  onClose,
  divisions,
  workspaceName,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  divisions: Division[];
  workspaceName: string;
  onCreated: (id: string) => void;
}) {
  const formId = useId();
  const qc = useQueryClient();
  const toast = useToast();
  const { copy, copied } = useCopy();
  const copyRef = useRef<HTMLButtonElement>(null);
  const formRef = useRef<HTMLFormElement>(null);
  const [role, setRole] = useState<Assignable>('member');
  const [divisionIds, setDivisionIds] = useState<string[]>([]);
  const [email, setEmail] = useState('');
  const [emailTouched, setEmailTouched] = useState(false);
  const [expiry, setExpiry] = useState<Expiry>('14');
  const [uses, setUses] = useState<Uses>('1');
  const [created, setCreated] = useState<WorkspaceInvite | null>(null);
  const shown = useRef(false);

  const emailValue = email.trim();
  const emailInvalid = emailValue !== '' && !isValidEmail(emailValue);

  // Move focus to the most useful control whenever the modal switches between form and ticket.
  useEffect(() => {
    if (!shown.current) {
      shown.current = true;
      return;
    }
    const t = window.setTimeout(() => {
      if (created) copyRef.current?.focus();
      else formRef.current?.querySelector<HTMLElement>('button, input')?.focus();
    }, 60);
    return () => window.clearTimeout(t);
  }, [created]);

  const create = useMutation({
    mutationFn: () =>
      api('POST /api/workspace/invites', {
        body: {
          role,
          divisionIds,
          email: emailValue || null,
          expiresInDays: expiry === 'never' ? null : Number(expiry),
          maxUses: emailValue ? 1 : uses === 'unlimited' ? null : Number(uses),
        },
      }),
    onSuccess: (r) => {
      setDetail(qc, (d) => ({ ...d, invites: [r.invite, ...d.invites.filter((x) => x.id !== r.invite.id)] }));
      void qc.invalidateQueries({ queryKey: qk.workspace });
      setCreated(r.invite);
      onCreated(r.invite.id);
    },
    onError: (e) => toast.error(e),
  });

  const submit = (e: FormEvent) => {
    e.preventDefault();
    setEmailTouched(true);
    if (emailInvalid || create.isPending) return;
    create.mutate();
  };

  const reset = () => {
    setRole('member');
    setDivisionIds([]);
    setEmail('');
    setEmailTouched(false);
    setExpiry('14');
    setUses('1');
    create.reset();
    setCreated(null);
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={created ? 'Invite ready' : `Invite people to ${workspaceName}`}
      description={created ? "Share this code with the people you're inviting." : 'Create a code people can use to join. You can revoke it at any time.'}
      footer={
        created ? (
          <>
            <Button onClick={reset}>Create another</Button>
            <Button variant="primary" onClick={onClose}>
              Done
            </Button>
          </>
        ) : (
          <>
            <Button onClick={onClose}>Cancel</Button>
            <Button type="submit" form={formId} variant="primary" loading={create.isPending} disabled={emailInvalid}>
              Create invite
            </Button>
          </>
        )
      }
    >
      {created ? (
        <InviteTicket invite={created} divisions={divisions} copy={copy} copied={copied} copyRef={copyRef} />
      ) : (
        <form ref={formRef} id={formId} onSubmit={submit} className="space-y-5">
          <Section title="Role" hint={role === 'admin' ? 'Can manage members, divisions and invites, and sees every account.' : 'Sees shared accounts plus those of their divisions.'}>
            <Segmented<Assignable>
              label="Role"
              value={role}
              onChange={setRole}
              options={[
                { value: 'member', label: 'Member' },
                { value: 'admin', label: 'Admin' },
              ]}
            />
          </Section>

          {divisions.length > 0 && (
            <Section title="Add to divisions" hint={role === 'admin' ? 'Optional — admins see every account anyway.' : 'They join these divisions automatically.'}>
              <div className="flex flex-wrap gap-1.5">
                {divisions.map((d) => (
                  <TogglePill key={d.id} checked={divisionIds.includes(d.id)} onChange={(on) => setDivisionIds((s) => toggle(s, d.id, on))}>
                    <Dot color={d.color} />
                    {d.name}
                  </TogglePill>
                ))}
              </div>
            </Section>
          )}

          <Field
            label="Only for this email (optional)"
            hint="When set, only this address can use the invite, and only once."
            error={emailTouched && emailInvalid ? 'Enter a valid email address.' : undefined}
          >
            {(id) => (
              <Input
                id={id}
                type="email"
                inputMode="email"
                autoComplete="off"
                placeholder="name@company.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onBlur={() => setEmailTouched(true)}
                className="max-w-sm"
              />
            )}
          </Field>

          <div className="grid gap-5 sm:grid-cols-2">
            <Section title="Expires">
              <Segmented<Expiry>
                size="sm"
                label="Expires after"
                value={expiry}
                onChange={setExpiry}
                options={[
                  { value: '7', label: '7 days' },
                  { value: '14', label: '14 days' },
                  { value: '30', label: '30 days' },
                  { value: 'never', label: 'Never' },
                ]}
              />
            </Section>
            <Section title="Can be used" hint={emailValue ? 'Email-locked invites work once.' : undefined}>
              <Segmented<Uses>
                size="sm"
                label="Maximum uses"
                value={emailValue ? '1' : uses}
                onChange={setUses}
                options={[
                  { value: '1', label: 'Once' },
                  { value: '10', label: '10 times', disabled: !!emailValue },
                  { value: 'unlimited', label: 'Unlimited', disabled: !!emailValue },
                ]}
              />
            </Section>
          </div>
        </form>
      )}
    </Modal>
  );
}

// ---------- leave ----------

function LeaveCard({ workspace, members, meId }: { workspace: WorkspaceSummary; members: WorkspaceMember[]; meId: string }) {
  const confirm = useConfirm();
  const toast = useToast();
  const enter = useEnterWorkspace();
  const others = members.filter((m) => m.userId !== meId);
  const alone = others.length === 0;
  // The server refuses to let the only owner leave while others remain: hand ownership over first.
  const blocked = workspace.role === 'owner' && !alone && !others.some((m) => m.role === 'owner');

  const leave = useMutation({
    mutationFn: () => api('POST /api/workspace/leave'),
    onSuccess: async () => {
      toast.success(alone ? `${workspace.name} was deleted` : `You left ${workspace.name}`);
      await enter('/inbox');
    },
    onError: (e) => toast.error(e),
  });

  const onLeave = async () => {
    const ok = await confirm(
      alone
        ? {
            title: `Leave and delete ${workspace.name}?`,
            message: "You're the only member, so leaving permanently deletes the organization with its email accounts, drafts and history. This can't be undone.",
            confirmLabel: 'Leave and delete',
            danger: true,
          }
        : {
            title: `Leave ${workspace.name}?`,
            message: "You'll lose access to its accounts straight away. To come back you'll need a new invite.",
            confirmLabel: 'Leave organization',
            danger: true,
          },
    );
    if (ok) leave.mutate();
  };

  return (
    <DangerCard icon={<LogOut className="h-5 w-5" aria-hidden />} title="Danger zone" description={alone ? 'Leaving as the only member deletes the organization.' : 'Leaving removes your access straight away.'}>
      <CardBody className="!pt-5">
        <SettingRow
          label={alone ? 'Leave and delete organization' : 'Leave organization'}
          description={
            blocked
              ? "You're the only owner. Make another member an owner first (change their role in Members), then you can leave."
              : alone
                ? "You're the only member, so leaving deletes the organization and everything in it."
                : "You'll lose access to its accounts. You can rejoin later with a new invite."
          }
          control={
            <Button size="sm" variant="danger" icon={<LogOut className="h-4 w-4" aria-hidden />} disabled={blocked} loading={leave.isPending} onClick={() => void onLeave()}>
              {alone ? 'Leave and delete' : 'Leave'}
            </Button>
          }
        />
      </CardBody>
    </DangerCard>
  );
}
